package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf

/**
 * The parts of the screens that only some roles see, each with the [Capability] it needs. The
 * app hides (never disables) what a role may not use, and view models do not load what it may
 * not read, so nobody meets "you don't have permission". View models expose the person's
 * capabilities at the screen's workplace; screens ask `capabilities.shows(Gate.X)`.
 *
 * Gates that [changesData] also need [Capability.MAKE_CHANGES]: once the workplace's plan ended
 * (SH-65) they hide, while the gates that only show something stay (read-only: owners and
 * managers see everything, employees their published shifts, nobody changes anything).
 *
 * This list is the map of who sees what (GatesTest checks every capability has a place in it).
 */
enum class Gate(val requires: Capability, val changesData: Boolean = false) {
    // --- Any member ---
    /** Own absences: the list of one's own (Absences screen). */
    OWN_ABSENCES(Capability.WORK_SHIFTS),
    /** Reporting an absence and withdrawing a pending one (Absences screen). */
    REPORT_ABSENCE(Capability.WORK_SHIFTS, changesData = true),
    /** Giving away, swapping and taking shifts, clocking in and out (dashboard, My shifts). */
    TRADE_SHIFTS(Capability.WORK_SHIFTS, changesData = true),
    /** Filling in one's availability (Availability). */
    SUBMIT_AVAILABILITY(Capability.WORK_SHIFTS, changesData = true),
    /** SH-118: the team's announcements (More, the notification of a post) and the notes on one's shifts. */
    ANNOUNCEMENTS(Capability.WORK_SHIFTS),
    /** SH-121: ticking the checklists of one's own shift (dashboard card, the shift's checklists). */
    TICK_CHECKLISTS(Capability.WORK_SHIFTS, changesData = true),
    /** SH-122: the workplace's policies, to open and read (Policies). */
    POLICIES(Capability.WORK_SHIFTS),
    /**
     * SH-122: "I have read it" (Policies). The person's own statement, so also once the plan
     * ended (acknowledgePolicy does not ask for one): it changes nothing of the workplace.
     */
    CONFIRM_POLICY(Capability.WORK_SHIFTS),
    /** SH-122: uploading one's own documents (profile → My documents). */
    UPLOAD_OWN_DOCUMENTS(Capability.WORK_SHIFTS, changesData = true),
    /**
     * SH-126: one's own courses and OHS training record (More → Training, the dashboard card):
     * taking a course and confirming an OHS instruction. The person's own training, so also once
     * the plan ended (submitCourse and confirmOhsTraining ask for none).
     */
    TAKE_COURSES(Capability.WORK_SHIFTS),
    /** SH-124: one's own share of the tips (More → Tips, "Your share"). */
    MY_TIPS(Capability.WORK_SHIFTS),

    // --- Managers ---
    /** The months and their schedules: opening a roster from a notification, the open-slots card (manager dashboard). */
    ROSTERS(Capability.EDIT_ROSTER),
    /** Creating and deleting months, the weekly skeleton (Rosters), building a month (roster builder). */
    ROSTER_EDITING(Capability.EDIT_ROSTER, changesData = true),
    /**
     * SH-90: "Suggest with AI" in the auto-assign sheet (pseudonymised data to Gemini). Costs the
     * owner's daily AI requests, so not once the plan ended; the server also checks the plan, the
     * kill switch and the quota (aiAssignStatus).
     */
    AI_AUTO_ASSIGN(Capability.EDIT_ROSTER, changesData = true),
    /** Publishing a month (roster builder). */
    PUBLISH_ROSTER(Capability.EDIT_ROSTER, changesData = true),
    /** Today's staff with their clock-in and clock-out times (both manager dashboards, SH-72). */
    TODAY_AT_WORK(Capability.EDIT_ROSTER),
    /**
     * SH-134: deciding on the "to review" list (approve, adjust to the plan, edit the times, confirm
     * or reject an unplanned clock-in); the list itself shows with [TODAY_AT_WORK].
     */
    CLOCK_REVIEW_DECIDING(Capability.EDIT_ROSTER, changesData = true),
    /** SH-257: "Godziny pracy": the days' shifts with their planned, clocked and confirmed hours (Money, More). */
    WORK_HOURS(Capability.EDIT_ROSTER),
    /** SH-257: confirming them (as planned, as clocked, typed), the day at once, who actually worked. */
    WORK_HOURS_EDITING(Capability.EDIT_ROSTER, changesData = true),
    /** SH-121: the workplace's checklists: who ticked what each day, the templates (Checklists). */
    CHECKLISTS(Capability.EDIT_ROSTER),
    /** SH-121: adding, changing and removing checklist templates. */
    CHECKLIST_EDITING(Capability.EDIT_ROSTER, changesData = true),
    /** The team's absence and swap requests (Absences, Requests, the dashboard's action inbox). */
    TEAM_REQUESTS(Capability.APPROVE_REQUESTS),
    /** Approving and rejecting absences and swaps. */
    DECIDE_REQUESTS(Capability.APPROVE_REQUESTS, changesData = true),
    /** The team: opening it from the dashboards, the people and their form. */
    TEAM(Capability.MANAGE_TEAM),
    /** Changing the team: adding people, their role, skills, permission, removal, invitations. */
    TEAM_EDITING(Capability.MANAGE_TEAM, changesData = true),
    /**
     * SH-47: who leads shifts, who needs supervision, the medical exam / OHS statuses (not the
     * dates) and availability notes of the team (team list, employee form, dashboards, roster).
     */
    STAFF_STATUS(Capability.MANAGE_TEAM),
    /** Changing shift lead, competence and availability notes (employee form). */
    STAFF_NOTES_EDITING(Capability.MANAGE_TEAM, changesData = true),
    /**
     * SH-118: posting to the team (announcements), pinning and deleting posts, notes on shifts
     * (roster builder's shift dialog). Managers only, as firestore.rules (isManager).
     */
    ANNOUNCEMENTS_POSTING(Capability.MANAGE_TEAM, changesData = true),
    /** SH-118: who read a post and who not yet (personal data: the workplace's managers). */
    READ_RECEIPTS(Capability.MANAGE_TEAM),
    /**
     * SH-125: hiring: the job posts and their applicants (Team → Hiring, a "new applicant"
     * notification). Applicants' data: the workplace's managers, as firestore.rules (isManager).
     */
    HIRING(Capability.MANAGE_TEAM),
    /** SH-125: writing, publishing, closing and deleting posts; moving and deleting applicants. */
    HIRING_EDITING(Capability.MANAGE_TEAM, changesData = true),
    /** SH-113: the team's annual leave balances and their CSV (Team → Leave balances). */
    LEAVE_BALANCES(Capability.MANAGE_TEAM),
    /** SH-113: setting a person's leave entitlement and carry-over. */
    LEAVE_BALANCES_EDITING(Capability.MANAGE_TEAM, changesData = true),
    /** SH-124: the workplace's tip pools with everyone's share, and their CSV (More → Tips). */
    TIP_POOLS(Capability.MANAGE_TEAM),
    /** SH-124: entering, changing and removing tip pools (also on the tablet with a manager's PIN). */
    TIP_POOLS_EDITING(Capability.MANAGE_TEAM, changesData = true),
    /** Availability campaigns (Rosters: "request availability"). */
    AVAILABILITY_CAMPAIGN(Capability.REQUEST_AVAILABILITY, changesData = true),
    /** Capacity before planning: demand, the team's availability, who has not answered (Rosters, SH-48). */
    CAPACITY(Capability.REQUEST_AVAILABILITY),
    /** The workplace's general settings, location, roles and skills, slots. */
    WORKPLACE_SETTINGS(Capability.EDIT_WORKPLACE_SETTINGS),
    /** Changing them, and the weekly blueprint. */
    SETTINGS_EDITING(Capability.EDIT_WORKPLACE_SETTINGS, changesData = true),
    /** SH-122: publishing and removing the workplace's policies (Policies). */
    POLICIES_EDITING(Capability.EDIT_WORKPLACE_SETTINGS, changesData = true),
    /** SH-122: who of the team confirmed a policy and when (Policies). */
    POLICY_READ_REPORT(Capability.MANAGE_TEAM),
    /** SH-126: the workplace's courses, who took them with what result, the team's OHS records (Training). */
    COURSE_PROGRESS(Capability.MANAGE_TEAM),
    /** SH-126: writing and publishing courses, assigning people, recording OHS instructions and exams. */
    MANAGE_COURSES(Capability.MANAGE_TEAM, changesData = true),
    /** E-mailing a published month to the team or one person (roster builder, SH-45). Sends mail for the workplace, so not once the plan ended. */
    EMAIL_ROSTER(Capability.EDIT_ROSTER, changesData = true),
    /**
     * SH-261: printing the whole team's roster (month or week) to pin up at work (roster builder).
     * Only shows something, so also once the plan ended; printRoster checks owner or manager.
     */
    PRINT_ROSTER(Capability.EDIT_ROSTER),
    /** Printing working-time reports (roster builder). */
    PRINT_WORKING_TIME(Capability.PRINT_WORKING_TIME),

    // --- Finances ---
    /** Money: the owner dashboard's labour %, revenue vs labour and charts, Financials, Daily Records. */
    MONEY(Capability.VIEW_FINANCES),
    /** Entering the day's takings and recalculating the figures (Daily Records, Financials). */
    MONEY_EDITING(Capability.VIEW_FINANCES, changesData = true),
    /** Pay terms and personal data of the team (employee form sections, contracted hours in the roster builder). */
    PAY_AND_PRIVATE_DATA(Capability.MANAGE_PAY),
    /** SH-147: "Set pay" for someone working without pay terms (the missing-pay warning). */
    PAY_EDITING(Capability.MANAGE_PAY, changesData = true),
    /**
     * SH-47: the team's medical exam and OHS training dates (health-related: owners and financial
     * managers, as firestore.rules' users/{uid}/hr), and the "block expired" workplace setting.
     */
    STAFF_RECORDS(Capability.MANAGE_PAY),
    /** Recording them. */
    STAFF_RECORDS_EDITING(Capability.MANAGE_PAY, changesData = true),
    /**
     * SH-122: the team's documents (contracts, medical and OHS certificates): the same readers as
     * the dates (firestore.rules / storage.rules users/{uid}/staff_documents).
     */
    STAFF_DOCUMENTS(Capability.MANAGE_PAY),
    /** Uploading and removing them. */
    STAFF_DOCUMENTS_EDITING(Capability.MANAGE_PAY, changesData = true),
    /**
     * SH-212: a person's timeline (hire, contracts, pay, statuses, reliefs, OHS, medical exams,
     * notes over time): the readers of the pay terms and HR dates (employee form → Timeline).
     */
    EMPLOYEE_TIMELINE(Capability.MANAGE_PAY),
    /** Adding, moving and removing its entries. */
    EMPLOYEE_TIMELINE_EDITING(Capability.MANAGE_PAY, changesData = true),
    /** Payroll and payslips (Financials' payroll button, the payroll screen, printing them). */
    PAYROLL(Capability.PAYROLL),
    /** Adding, changing and removing bonuses, deductions and expense refunds (labour cost screen, SH-78). */
    ADJUSTMENTS_EDITING(Capability.PAYROLL, changesData = true),
    /** The workplace's financial settings, payroll settings and sales targets. */
    WORKPLACE_FINANCES(Capability.EDIT_WORKPLACE_FINANCES),
    /** Changing them. */
    WORKPLACE_FINANCES_EDITING(Capability.EDIT_WORKPLACE_FINANCES, changesData = true),

    // --- The owner ---
    /** Recurring costs (profile, cost rules screen): the owner's own rules, not a workplace's data. */
    RECURRING_COSTS(Capability.MANAGE_COST_RULES),
    /** Removing the workplace (workplace settings): also after the plan ended (GDPR). */
    DELETE_WORKPLACE(Capability.DELETE_WORKPLACE),
    /** The plan and subscription (profile): above all once it ended. */
    PLAN(Capability.BILLING)
}

/** Whether these capabilities show [gate]. */
fun Set<Capability>.shows(gate: Gate): Boolean =
    gate.requires in this && (!gate.changesData || Capability.MAKE_CHANGES in this)

/** Whether [workplaceId]'s plan allows changes (SH-65); always without the use case (tests, previews). */
fun ObservePlanActiveUseCase?.activeAt(workplaceId: String?): Flow<Boolean> = this?.invoke(workplaceId) ?: flowOf(true)

/** [activeAt], once: what a screen loading its data snapshot asks. */
suspend fun ObservePlanActiveUseCase?.activeNow(workplaceId: String?): Boolean = this?.isActive(workplaceId) ?: true

/**
 * What [user] may do at [workplaceId] as its plan leaves it, kept current: the capabilities view
 * models expose (an ended plan drops [Capability.MAKE_CHANGES], see Capability.withPlan).
 */
fun capabilitiesAt(user: Flow<User?>, plans: ObservePlanActiveUseCase?, workplaceId: String?): Flow<Set<Capability>> =
    combine(user, plans.activeAt(workplaceId)) { person, planActive -> person?.capabilitiesAt(workplaceId, planActive).orEmpty() }
        .distinctUntilChanged()

/** Which dashboard a person gets at a workplace: the richest their capabilities allow. */
enum class DashboardKind { OWNER, MANAGER, EMPLOYEE, ENDED }

fun dashboardKindFor(capabilities: Set<Capability>): DashboardKind = when {
    Capability.VIEW_FINANCES in capabilities -> DashboardKind.OWNER
    Capability.EDIT_ROSTER in capabilities -> DashboardKind.MANAGER
    Capability.WORK_SHIFTS in capabilities -> DashboardKind.EMPLOYEE
    // An ended engagement may do nothing (the rules require isActive).
    else -> DashboardKind.ENDED
}
